class FavoritesUrlManager {
  static base64Encode(str) {
    return btoa(encodeURIComponent(str));
  }

  static base64Decode(str) {
    return decodeURIComponent(atob(str));
  }

  static getFavoritesFromUrl() {
    const url = new URL(window.location);
    const encodedProductData = url.searchParams.get('favorites');
    if (!encodedProductData) {
      return [];
    }
    const decodedProductData = this.base64Decode(encodedProductData);
    return JSON.parse(decodedProductData);
  }

  static updateUrl(favorites) {
    if (favorites.length === 0) {
      const url = new URL(window.location);
      url.searchParams.delete('favorites');
      window.history.replaceState({}, '', url);
      return;
    }

    const productIds = this.base64Encode(JSON.stringify(favorites));
    const url = new URL(window.location);
    url.searchParams.set('favorites', productIds);
    window.history.replaceState({}, '', url);
  }

  static clearSearchParameters() {
    const url = new URL(window.location.href);
    url.search = '';
    window.history.replaceState({}, '', url.href);
  }
}

sessionStorage.setItem('firstLoadFavorites', true);

class Favorites extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegateElement = new Delegate(this.element);
    this.delegateBody = new Delegate(document.body);

    this.shouldLoadFromUrl = false;

    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache timeout
    this.cachePrefix = 'favorites_';

    this.options = JSON.parse(
      this.element.getAttribute('data-section-settings')
    );

    this.favoritesHeader = document.querySelector('favorites-header');
    this.gridElement = this.element.querySelector('.favorites__grid');
    this.shareButton = this.element.querySelector(
      '.favorites__share-button-container'
    );
    this.paginationWrapper = this.element.querySelector(
      '.favorites__pagination-wrapper'
    );
    this.paginationPrev = this.element.querySelector(
      '.favorites__pagination-prev'
    );
    this.paginationNext = this.element.querySelector(
      '.favorites__pagination-next'
    );
    this.paginationNumbers = this.element.querySelector(
      '.favorites__pagination-numbers'
    );
    this.totalFavorites = this.element.querySelector('.favorites__total');
    this.subtotalFavorites = this.element.querySelector('.favorites__subtotal');
    this.spinningLoader = this.element.querySelector('.favorites__grid-container .spinningLoader');

    this.currentPage = 1;
    this.productsPerPage = this.options.productsPerPage || 8;
    this.disableShowingAddedProducts = false;
    this.favoritesEmpty = document.querySelector('.favorites__empty');
    
    if (sessionStorage.getItem('firstLoadFavorites') === 'true') {
      sessionStorage.setItem('firstLoadFavorites', false);
      this.renderContent();
      this.bindEvents();
      new AddToCart(this.element);
    } else {
      return;
    }
  }

  bindEvents() {
    if (!this.disableShowingAddedProducts) {
      this.delegateBody.on('favorite:remove', this.removeFavorite.bind(this));

      this.delegateBody.on('invalid', () => {
        this.showSnackbar('invalid');
      });
    }

    this.delegateElement.on(
      'click',
      '[data-action="copy-link"]',
      this.copyUrlToClipboard.bind(this)
    );

    // Pagination event handlers
    this.delegateElement.on(
      'click',
      '[data-action="pagination-prev"]',
      this.handlePrevPage.bind(this)
    );

    this.delegateElement.on(
      'click',
      '[data-action="pagination-next"]',
      this.handleNextPage.bind(this)
    );

    this.delegateElement.on(
      'click',
      '[data-action="pagination-number"]',
      this.handlePageNumberClick.bind(this)
    );

    window.addEventListener(
      'beforeunload',
      FavoritesUrlManager.clearSearchParameters.bind(FavoritesUrlManager)
    );
  }

  renderContent() {
    this.compareFavorites();

    const urlFavorites = this.getFavoritesFromUrl();
    const localFavorites = this.getFavorites();

    if (urlFavorites.length > 0 && this.disableShowingAddedProducts) {
      this.shouldLoadFromUrl = true;
    }

    const favoritesTotal = this.getFavoritesToShow().length;

    if (favoritesTotal > 0) {
      this.loadPage(1);

      if (!this.disableShowingAddedProducts && localFavorites.length > 0) {
        this.updateUrl();
      }
    } else {
      if (this.favoritesEmpty) {
        this.favoritesEmpty.style.display = 'flex';
        this.favoritesEmpty.ariaHidden = 'false';
      }
    }
  }

  checkIfEmpty() {
    const items = this.getFavoritesToShow();
    const lastElement = document.querySelector(
      '#MainContent .shopify-section:last-child'
    );

    if (items.length === 0) {
      this.gridElement.parentElement.style.display = 'none';
      if (this.shareButton) { 
        this.shareButton.style.display = 'none';
        this.shareButton.ariaHidden = 'true';
      }
      this.paginationWrapper.style.display = 'none';
      this.paginationWrapper.ariaHidden = 'true';
      if (lastElement && !lastElement.id.includes('main-favorites')) {
        lastElement.style.display = '';
      }
      this.favoritesEmpty.style.display = 'flex';
    } else {
      this.gridElement.parentElement.style.display = '';
      if (this.shareButton) {
        this.shareButton.style.display = '';
        this.shareButton.ariaHidden = 'false';
      }
      this.paginationWrapper.style.display = '';
      this.paginationWrapper.ariaHidden = 'false';
      if (lastElement && !lastElement.id.includes('main-favorites')) {
        lastElement.style.display = 'none';
      }
    }
  }

  cacheKey(startIndex, endIndex, favorites) {
    const favoritesHash = this.hashFavorites(favorites);
    return `${this.cachePrefix}${favoritesHash}_${startIndex}_${endIndex}`;
  }

  hashFavorites(favorites) {
    return btoa(JSON.stringify(favorites.map(f => f.id).sort())).slice(0, 16);
  }

  getCachedData(startIndex, endIndex, favorites) {
    const key = this.cacheKey(startIndex, endIndex, favorites);
    const cached = this.cache.get(key);

    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    if (cached) {
      this.cache.delete(key);
    }

    return null;
  }

  setCachedData(startIndex, endIndex, favorites, data) {
    const key = this.cacheKey(startIndex, endIndex, favorites);
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    });
  }

  clearCache(pattern = null) {
    if (pattern) {
      const keysToDelete = [];
      for (const [key] of this.cache) {
        if (key.startsWith(this.cachePrefix + pattern)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      // Clear all cache
      this.cache.clear();
    }
  }

  // Pagination methods
  loadPage(pageNumber) {
    this.spinningLoader.classList.add('is-visible');

    const favorites = this.getFavoritesToShow();
    const totalPages = Math.ceil(favorites.length / this.productsPerPage);

    if (pageNumber < 1 || (pageNumber > totalPages && totalPages > 0)) {
      return;
    }

    this.currentPage = pageNumber;
    const startIndex = (pageNumber - 1) * this.productsPerPage;
    const endIndex = startIndex + this.productsPerPage;

    this.rerenderFavorites(startIndex, endIndex, true);
    this.updatePaginationUI(totalPages);
    this.updateProductCount(startIndex, endIndex, favorites.length);
    this.checkIfEmpty();
  }

  handlePrevPage(e) {
    e.preventDefault();
    if (this.currentPage > 1) {
      this.loadPage(this.currentPage - 1);
    }
  }

  handleNextPage(e) {
    e.preventDefault();
    const favorites = this.getFavoritesToShow();
    const totalPages = Math.ceil(favorites.length / this.productsPerPage);

    if (this.currentPage < totalPages) {
      this.loadPage(this.currentPage + 1);
    }
  }

  handlePageNumberClick(e) {
    e.preventDefault();
    const pageNumber = parseInt(e.target.getAttribute('data-page'));
    if (pageNumber && pageNumber !== this.currentPage) {
      this.loadPage(pageNumber);
    }
  }

  updatePaginationUI(totalPages) {
    this.paginationPrev.disabled = this.currentPage <= 1;
    this.paginationNext.disabled = this.currentPage >= totalPages;

    this.paginationPrev.classList.toggle('favorites__pagination-prev--disabled', this.currentPage <= 1);
    this.paginationNext.classList.toggle('favorites__pagination-next--disabled', this.currentPage >= totalPages);

    this.generatePageNumbers(totalPages);
  }

  generatePageNumbers(totalPages) {
    this.paginationNumbers.innerHTML = '';

    if (totalPages <= 1) {
      this.paginationNext.style.display = 'none';
      this.paginationPrev.style.display = 'none';
      return;
    }

    const maxVisiblePages = 5;
    const maxVisiblePagesEdge = 3;
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxVisiblePages) {
      if (this.currentPage === 1) {
        endPage = maxVisiblePagesEdge;
      } else if (this.currentPage === totalPages) {
        startPage = endPage - maxVisiblePagesEdge + 1;
      } else if (this.currentPage <= 3) {
        endPage = maxVisiblePages;
      } else if (this.currentPage >= totalPages - 2) {
        startPage = totalPages - maxVisiblePages + 1;
      } else {
        startPage = this.currentPage - 2;
        endPage = this.currentPage + 2;
      }
    }

    if (startPage > 1) {
      this.addPageNumber(1, totalPages);
      if (startPage > 2) {
        this.addEllipsis();
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      this.addPageNumber(i, totalPages, i === this.currentPage);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        this.addEllipsis();
      }
      this.addPageNumber(totalPages, totalPages);
    }
  }

  addPageNumber(pageNumber, totalPages, isCurrent = false) {
    const li = document.createElement('li');
    li.className = 'favorites__pagination-item-wrapper';

    const button = document.createElement('button');
    button.className = `favorites__pagination-item u-s3${isCurrent ? ' favorites__pagination-item--current' : ''
      }`;
    button.textContent = pageNumber;
    button.setAttribute('data-page', pageNumber);
    button.setAttribute('data-action', 'pagination-number');
    button.ariaLabel = "Page " + pageNumber + " of " + totalPages;

    if (isCurrent) {
      button.setAttribute('aria-current', 'page');
    }

    li.appendChild(button);
    this.paginationNumbers.appendChild(li);
  }

  addEllipsis() {
    const li = document.createElement('li');
    li.className = 'favorites__pagination-item-wrapper';

    const span = document.createElement('span');
    span.className = 'favorites__pagination-item';
    span.textContent = '...';

    li.appendChild(span);
    this.paginationNumbers.appendChild(li);
  }

  updateProductCount(startIndex, endIndex, totalCount) {
    const productsShownSoFar = Math.min(endIndex, totalCount);

    this.subtotalFavorites.innerHTML = productsShownSoFar;
    this.totalFavorites.innerHTML = totalCount;
  }

  showAndUpdateProductCount() {
    const favorites = this.getFavoritesToShow();
    const startIndex = (this.currentPage - 1) * this.productsPerPage;
    const endIndex = startIndex + this.productsPerPage;

    this.updateProductCount(startIndex, endIndex, favorites.length);
  }

  compareFavorites() {
    if (
      !(
        this.arraysEqual(this.getFavoritesFromUrl(), this.getFavorites()) ||
        this.getFavoritesFromUrl().length === 0
      )
    ) {
      this.disableShowingAddedProducts = true;
    }
  }

  arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    arr1.sort((a, b) => a.id.localeCompare(b.id));
    arr2.sort((a, b) => a.id.localeCompare(b.id));
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i].id !== arr2[i].id) return false;
    }
    return true;
  }

  updateUrl() {
    FavoritesUrlManager.updateUrl(this.getFavorites());
  }

  removeFavorite(e) {
    this.lastRemovedProduct = e.detail;

    const currentHeight = this.gridElement.offsetHeight;
    this.gridElement.style.height = currentHeight + 'px';
    this.gridElement.classList.add('favorites__grid--height-locked');
    this.gridElement.offsetHeight;

    setTimeout(() => {
      const productElement = this.element.querySelector(
        `[data-id="${e.detail.id}"]`
      );
      if (productElement) {
        productElement.closest('.product-card').remove();
      }

      this.checkIfEmpty();

      if (!this.disableShowingAddedProducts) {
        this.updateUrl();
      }

      this.clearCache();

      const totalFavorites = this.getFavoritesToShow().length;
      const totalPages = Math.ceil(totalFavorites / this.productsPerPage);

      if (this.currentPage > totalPages && totalPages > 0) {
        this.currentPage = totalPages;
      }

      this.loadPage(this.currentPage);
    }, 200);
  }

  toggleFavorites() {
    this.checkIfEmpty();
    this.favoritesHeader.toggleFavorites();
  }

  getFavorites() {
    return FavoritesStorage.getFavorites();
  }

  getFavoritesFromUrl() {
    return FavoritesUrlManager.getFavoritesFromUrl();
  }

  getFavoritesToShow() {
    var items;

    if (this.disableShowingAddedProducts) {
      items = this.getFavoritesFromUrl();
    } else {
      items = this.getFavorites();
    }

    return items;
  }

  getSearchQueryStrings(startIndex, endIndex) {
    const items = this.getFavoritesToShow();
    const bucketSize = 10;
    const limitedItems = items.slice(startIndex, endIndex);

    this.currentIndex = endIndex;
    const idBuckets = Array.from(
      { length: Math.ceil(limitedItems.length / bucketSize) },
      (_, i) =>
        limitedItems
          .slice(i * bucketSize, (i + 1) * bucketSize)
          .map((item) => `id:${item.id}`)
          .join(' OR ')
    );

    return idBuckets;
  }

  async rerenderFavorites(startIndex, endIndex, clear) {
    const currentHeight = this.gridElement.offsetHeight;
    this.gridElement.style.height = currentHeight + 'px';
    this.gridElement.classList.add('favorites__grid--height-locked');
    this.gridElement.offsetHeight;

    if (
      this.currentIndex >= this.getFavoritesToShow().length &&
      this.scrollListener
    ) {
      this.scrollListener.removeScrollListener();
      return;
    }

    const favorites = this.getFavoritesToShow();

    // Check cache first
    const cachedData = this.getCachedData(startIndex, endIndex, favorites);
    if (cachedData) {
      if (clear) {
        this.gridElement.innerHTML = '';
      }
      this.appendContent(cachedData);
      return true;
    }

    const queryStrings = this.getSearchQueryStrings(startIndex, endIndex);

    if (clear) {
      this.gridElement.innerHTML = '';
    }

    if (queryStrings.length === 0) {
      try {
        const response = await fetch('/?section_id=main-favorites');
        const html = await response.text();

        this.setCachedData(startIndex, endIndex, favorites, html);

        this.appendContent(html);
      } catch (error) {
        console.error('Error fetching main favorites section:', error);
      }
      return;
    }

    try {
      const promises = queryStrings.map((string) =>
        fetch(`${window.routes.localeRootUrl}/search?view=favorites&type=product&q=${string}`, {
          credentials: "same-origin",
          method: "GET",
        })
      );

      let combinedHtml = '';
      for (const promise of promises) {
        const response = await promise;
        const content = await response.text();
        combinedHtml += content;
      }

      this.setCachedData(startIndex, endIndex, favorites, combinedHtml);
      this.appendContent(combinedHtml);

    } catch (error) {
      console.error('Error fetching favorites:', error);
    }

    return true;
  }

  appendContent(html, one) {
    this.spinningLoader.classList.remove('is-visible');

    const temp = document.createElement('div');
    temp.innerHTML = html;

    const contentElements = temp.querySelectorAll(
      '.Search--Result, .favorites__grid, [data-search-results]'
    );

    if (!contentElements.length) {
      console.error('Could not find content element in HTML response');
      return;
    }

    if (!one) {
      contentElements.forEach((el) => {
        while (el.firstElementChild) {
          this.gridElement.appendChild(el.firstElementChild);
        }
      });
    } else {
      const first = contentElements[0];
      if (first?.firstElementChild) {
        this.gridElement.prepend(first.firstElementChild);
      }
    }

    this.toggleFavorites();
    this.showAndUpdateProductCount();
    if (!this.disableShowingAddedProducts) this.updateUrl();

    setTimeout(() => {
      this.gridElement.style.height = '';
      this.gridElement.classList.remove('favorites__grid--height-locked');

      window.scrollTo({top: 0, behavior: 'smooth'});
    }, 50);
  }

  async copyUrlToClipboard() {
    if (!this.disableShowingAddedProducts) {
      this.updateUrl();
    }

    const url = window.location.href;

    const urlObj = new URL(url);
    const favoritesParam = urlObj.searchParams.get('favorites');

    if (!favoritesParam && this.getFavorites().length > 0) {
      console.warn(
        'URL does not contain favorites data - sharing may not work properly'
      );
    }

    try {
      await navigator.clipboard.writeText(url);
      this.showSnackbar('copy');
    } catch (error) {
      this.fallbackCopyToClipboard(url);
    }

    // GTM: custom_favorites_shared
    if (window.dataLayer) {
      dataLayer.push({ event: 'custom_favorites_shared', favorites_count: this.getFavorites().length });
    }
  }

  fallbackCopyToClipboard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.showSnackbar('copy');
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }

    document.body.removeChild(textArea);
  }

  showSnackbar(identifier) {
    this.snackbar = this.element.querySelector(
      `.favorites__snackbar--${identifier}`
    );
    this.snackbar.setAttribute('aria-hidden', 'false');

    const clickHandler = () => {
      clearTimeout(this.snackbarTimeout);
      this.snackbar.setAttribute('aria-hidden', 'true');
      this.delegateBody.off('click', clickHandler);
    };

    this.delegateBody.on('click', clickHandler);

    if (this.snackbarTimeout) {
      clearTimeout(this.snackbarTimeout);
    }

    this.snackbarTimeout = setTimeout(() => {
      this.snackbar.setAttribute('aria-hidden', 'true');
    }, 3000);
  }
}

customElements.define('favorites-section', Favorites);